import { z } from 'zod';

// ---------------------------------------------------------------------------
// Discord server bot-install connection schemas.
//
// Mirrors the discord_connections / discord_oauth_states tables added in
// migration 0010. Tokens (oauth_access_token, oauth_refresh_token) are NOT
// part of the client-facing shape - they're column-level revoked from
// authenticated/anon and only the bot + edge function ever read them.
// ---------------------------------------------------------------------------

export const DiscordConnectionStatusSchema = z.enum([
  'pending',
  'connected',
  'needs_attention',
  'disconnected',
]);
export type DiscordConnectionStatus = z.infer<typeof DiscordConnectionStatusSchema>;

// Client-facing camelCase shape returned to the portal.
export const DiscordConnectionSchema = z.object({
  id: z.string().uuid(),
  studioId: z.string().uuid(),
  guildId: z.string().min(1),
  guildName: z.string().min(1),
  guildIconUrl: z.string().nullable(),
  botPermissions: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === 'number' ? BigInt(v).toString() : v)),
  status: DiscordConnectionStatusSchema,
  oauthScopes: z.array(z.string()),
  // @deprecated The authoritative watched-channel set moved to the
  // per-game `game_watched_channels` table in migration 0082; the
  // connection's array is no longer read by the picker, bot, or
  // Insights. Kept on the schema until a follow-up migration drops the
  // column, so existing realtime payloads that still carry it don't
  // fail Zod.
  watchedChannelIds: z.array(z.string()).default([]),
  lastHealthCheckAt: z.string().datetime({ offset: true }).nullable(),
  connectedByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type DiscordConnection = z.infer<typeof DiscordConnectionSchema>;

// Raw DB row -> client shape transform. bot_permissions arrives as a string
// from supabase-js (bigint columns serialise as string to avoid precision
// loss). oauth_access_token / oauth_refresh_token / oauth_expires_at are
// deliberately absent: column-level grants prevent the portal from selecting
// them, and the bot uses a dedicated row-typed helper to read them with the
// service role.
export const DiscordConnectionRowSchema = z.object({
  id: z.string().uuid(),
  studio_id: z.string().uuid(),
  guild_id: z.string().min(1),
  guild_name: z.string().min(1),
  guild_icon_url: z.string().nullable(),
  bot_permissions: z.union([z.string(), z.number()]),
  oauth_scopes: z.array(z.string()),
  watched_channel_ids: z.array(z.string()).default([]),
  status: DiscordConnectionStatusSchema,
  last_health_check_at: z.string().datetime({ offset: true }).nullable(),
  connected_by_user_id: z.string().uuid().nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});

export const DiscordConnectionRowResponseSchema = DiscordConnectionRowSchema.transform((row) => ({
  id: row.id,
  studioId: row.studio_id,
  guildId: row.guild_id,
  guildName: row.guild_name,
  guildIconUrl: row.guild_icon_url,
  botPermissions:
    typeof row.bot_permissions === 'number'
      ? BigInt(row.bot_permissions).toString()
      : row.bot_permissions,
  status: row.status,
  oauthScopes: row.oauth_scopes,
  watchedChannelIds: row.watched_channel_ids,
  lastHealthCheckAt: row.last_health_check_at,
  connectedByUserId: row.connected_by_user_id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
}));

// ---------------------------------------------------------------------------
// discord-oauth edge function payloads
// ---------------------------------------------------------------------------

export const DiscordOAuthStartBodySchema = z.object({
  action: z.literal('start'),
  studioId: z.string().uuid(),
  // Always required today: a connect always happens in a game context. Edge
  // function will validate the caller is a member of the studio that owns
  // this game.
  gameId: z.string().uuid(),
});
export type DiscordOAuthStartBody = z.infer<typeof DiscordOAuthStartBodySchema>;

export const DiscordOAuthStartResponseSchema = z.object({
  url: z.string().url(),
  state: z.string().min(1),
});
export type DiscordOAuthStartResponse = z.infer<typeof DiscordOAuthStartResponseSchema>;

export const DiscordOAuthCallbackBodySchema = z.object({
  action: z.literal('callback'),
  code: z.string().min(1),
  state: z.string().min(1),
  guildId: z.string().min(1),
});
export type DiscordOAuthCallbackBody = z.infer<typeof DiscordOAuthCallbackBodySchema>;

// Discriminated by `outcome`. The portal narrows on this to render the right
// follow-up: success-attached, success-reused, or the friendly block message.
export const DiscordOAuthCallbackResponseSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('created'),
    connection: DiscordConnectionSchema,
    gameId: z.string().uuid(),
  }),
  z.object({
    outcome: z.literal('reused_in_studio'),
    connection: DiscordConnectionSchema,
    gameId: z.string().uuid(),
    // True when the same studio already had this guild attached to a different
    // game; false when this is the first time the guild's connection is being
    // attached to a game.
    alreadyAttachedToOtherGame: z.boolean(),
  }),
  z.object({
    outcome: z.literal('blocked_other_studio'),
  }),
]);
export type DiscordOAuthCallbackResponse = z.infer<typeof DiscordOAuthCallbackResponseSchema>;

export const DiscordOAuthBodySchema = z.discriminatedUnion('action', [
  DiscordOAuthStartBodySchema,
  DiscordOAuthCallbackBodySchema,
]);
export type DiscordOAuthBody = z.infer<typeof DiscordOAuthBodySchema>;

// ---------------------------------------------------------------------------
// NOTIFY payloads
// ---------------------------------------------------------------------------

export const DiscordConnectionNotifyPayloadSchema = z.object({
  connectionId: z.string().uuid(),
  studioId: z.string().uuid(),
  guildId: z.string().min(1),
  status: DiscordConnectionStatusSchema,
  previousStatus: DiscordConnectionStatusSchema.nullable(),
  updatedAt: z.string().datetime({ offset: true }),
});
export type DiscordConnectionNotifyPayload = z.infer<typeof DiscordConnectionNotifyPayloadSchema>;

export const DiscordConnectionRecheckPayloadSchema = z.object({
  connectionId: z.string().uuid(),
});
export type DiscordConnectionRecheckPayload = z.infer<typeof DiscordConnectionRecheckPayloadSchema>;

// Emitted by disconnect_discord_connection so the bot leaves the Discord
// guild after the user disconnects from the portal. Discord has no API for
// "leave when the user asks me to" so we mediate via NOTIFY.
export const DiscordConnectionLeavePayloadSchema = z.object({
  guildId: z.string().min(1),
});
export type DiscordConnectionLeavePayload = z.infer<typeof DiscordConnectionLeavePayloadSchema>;

// Emitted whenever a row in game_watched_channels changes (trigger added
// in migration 0082). The bot uses this to refresh its in-memory
// subscription set without re-reading on every messageCreate. Carries
// the game whose set changed so the mirror can do a scoped refresh
// rather than a full reload.
export const GameWatchedChannelsChangedPayloadSchema = z.object({
  gameId: z.string().uuid(),
  studioId: z.string().uuid().nullable(),
  guildId: z.string().min(1).nullable(),
});
export type GameWatchedChannelsChangedPayload = z.infer<
  typeof GameWatchedChannelsChangedPayloadSchema
>;

// Emitted by request_discord_channels_resync (migration 0013). Picker's
// "Refresh" affordance fires this so the bot re-fetches a guild's channel
// list when a private channel was just made readable.
export const DiscordChannelsResyncPayloadSchema = z.object({
  connectionId: z.string().uuid(),
  guildId: z.string().min(1),
});
export type DiscordChannelsResyncPayload = z.infer<typeof DiscordChannelsResyncPayloadSchema>;

// Emitted by request_discord_channel_backfill. The picker's "Bring over
// history going back N days" opt-in fires this; the bot streams up to N days
// of past messages from each channel through the inspector.
//
// jobId is present for the game-scoped overload (migration 0118): the bot
// updates that discord_backfill_job row as it works so the portal can show
// start / finish / fail. It's optional so a NOTIFY from the original 0015
// three-arg function (no job row) still parses.
export const DiscordChannelBackfillPayloadSchema = z.object({
  jobId: z.string().uuid().optional(),
  connectionId: z.string().uuid(),
  guildId: z.string().min(1),
  channelIds: z.array(z.string().min(1)).min(1),
  days: z.number().int().min(1).max(30),
});
export type DiscordChannelBackfillPayload = z.infer<typeof DiscordChannelBackfillPayloadSchema>;

// A row of public.discord_backfill_job (migration 0118). The portal reads
// these under member RLS and subscribes via realtime; status drives the
// toast + inline "Importing history..." surface on the watched-channels page.
export const DiscordBackfillJobStatusSchema = z.enum([
  'pending',
  'running',
  'done',
  'failed',
]);
export type DiscordBackfillJobStatus = z.infer<typeof DiscordBackfillJobStatusSchema>;

export const DiscordBackfillJobSchema = z.object({
  id: z.string().uuid(),
  studioId: z.string().uuid(),
  gameId: z.string().uuid(),
  connectionId: z.string().uuid(),
  channelIds: z.array(z.string()),
  days: z.number().int(),
  status: DiscordBackfillJobStatusSchema,
  channelsTotal: z.number().int().nonnegative(),
  channelsDone: z.number().int().nonnegative(),
  totalMessages: z.number().int().nonnegative(),
  error: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type DiscordBackfillJob = z.infer<typeof DiscordBackfillJobSchema>;

// ---------------------------------------------------------------------------
// Guild channel mirror (table public.discord_guild_channels - migration 0013)
//
// Bot-maintained snapshot of readable text/forum/announcement channels in a
// connected guild, plus any threads it's actively tracking. Rendered by the
// portal's channel picker. Voice/stage/category channels are out of scope.
// ---------------------------------------------------------------------------

export const DiscordGuildChannelTypeSchema = z.enum([
  'text',
  'forum',
  'announcement',
  'thread',
]);
export type DiscordGuildChannelType = z.infer<typeof DiscordGuildChannelTypeSchema>;

export const DiscordGuildChannelRowSchema = z.object({
  id: z.string().uuid(),
  connection_id: z.string().uuid(),
  channel_id: z.string().min(1),
  name: z.string().min(1),
  type: DiscordGuildChannelTypeSchema,
  parent_id: z.string().nullable(),
  parent_name: z.string().nullable(),
  position: z.number().int(),
  last_synced_at: z.string().datetime({ offset: true }),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});

export const DiscordGuildChannelSchema = z.object({
  id: z.string().uuid(),
  connectionId: z.string().uuid(),
  channelId: z.string().min(1),
  name: z.string().min(1),
  type: DiscordGuildChannelTypeSchema,
  parentId: z.string().nullable(),
  parentName: z.string().nullable(),
  position: z.number().int(),
  lastSyncedAt: z.string().datetime({ offset: true }),
});
export type DiscordGuildChannel = z.infer<typeof DiscordGuildChannelSchema>;

export const DiscordGuildChannelRowResponseSchema = DiscordGuildChannelRowSchema.transform(
  (row) => ({
    id: row.id,
    connectionId: row.connection_id,
    channelId: row.channel_id,
    name: row.name,
    type: row.type,
    parentId: row.parent_id,
    parentName: row.parent_name,
    position: row.position,
    lastSyncedAt: row.last_synced_at,
  }),
);

export const DISCORD_GUILD_CHANNEL_CLIENT_COLUMNS: readonly string[] = Object.keys(
  DiscordGuildChannelRowSchema.shape,
);

export const DISCORD_GUILD_CHANNEL_CLIENT_COLUMNS_CSV =
  DISCORD_GUILD_CHANNEL_CLIENT_COLUMNS.join(', ');

// ---------------------------------------------------------------------------
// Guild role mirror (table public.discord_guild_roles - migration 0041)
//
// Bot-maintained snapshot of every role in a connected guild. Powers the
// upcoming `recruit` field's role picker (Story 10B). The mirror includes
// `managed` so the picker can hide bot/integration-owned roles, and a
// `#RRGGBB` colour string so the picker can render Discord-style swatches.
// ---------------------------------------------------------------------------

export const DiscordGuildRoleRowSchema = z.object({
  id: z.string().uuid(),
  connection_id: z.string().uuid(),
  external_id: z.string().min(1),
  name: z.string().min(1),
  position: z.number().int(),
  managed: z.boolean(),
  hoist: z.boolean(),
  color_hex: z.string().nullable(),
  last_synced_at: z.string().datetime({ offset: true }),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});

export const DiscordGuildRoleSchema = z.object({
  id: z.string().uuid(),
  connectionId: z.string().uuid(),
  externalId: z.string().min(1),
  name: z.string().min(1),
  position: z.number().int(),
  managed: z.boolean(),
  hoist: z.boolean(),
  colorHex: z.string().nullable(),
  lastSyncedAt: z.string().datetime({ offset: true }),
});
export type DiscordGuildRole = z.infer<typeof DiscordGuildRoleSchema>;

export const DiscordGuildRoleRowResponseSchema = DiscordGuildRoleRowSchema.transform((row) => ({
  id: row.id,
  connectionId: row.connection_id,
  externalId: row.external_id,
  name: row.name,
  position: row.position,
  managed: row.managed,
  hoist: row.hoist,
  colorHex: row.color_hex,
  lastSyncedAt: row.last_synced_at,
}));

export const DISCORD_GUILD_ROLE_CLIENT_COLUMNS: readonly string[] = Object.keys(
  DiscordGuildRoleRowSchema.shape,
);

export const DISCORD_GUILD_ROLE_CLIENT_COLUMNS_CSV =
  DISCORD_GUILD_ROLE_CLIENT_COLUMNS.join(', ');

// ---------------------------------------------------------------------------
// Bot install permission set
//
// The integer baked into the Discord install URL. Stored as a constant so
// the edge function URL composition and the bot's GUILD_UPDATE permissions
// drift detection use the same source of truth.
//
// Bits, in order of bit position:
//   * Manage Channels       (16)            -- recruit channel overwrites
//   * View Channels         (1024)
//   * Send Messages         (2048)
//   * Read Message History  (65536)
//   * Manage Roles          (268435456)     -- recruit role grants
//   * Send in Threads       (274877906944)
//   * Use App Commands      (2147483648)
//
// Total = 277293835280. Manage Roles + Manage Channels are required for
// the recruit opt-in (Story 10B): the bot grants ViewChannel +
// SendMessages overwrites on each configured channel and adds each
// configured role to the submitter. Granting them at install time
// avoids a per-feature reconnect prompt for the common case; the
// per-form pre-flight in form_perm_check_listener still catches the
// edge cases where a studio later revokes a perm or stacks a role
// above the bot's own.
// ---------------------------------------------------------------------------

export const DISCORD_BOT_INSTALL_PERMISSIONS = '277293835280';
export const DISCORD_BOT_INSTALL_SCOPES: readonly string[] = ['bot', 'applications.commands'];

// ---------------------------------------------------------------------------
// Pure helpers - safe to import from Node-side code (bot, portal). The edge
// function (Deno) keeps its own copy with a sync-comment because Deno can't
// import from this package today.
// ---------------------------------------------------------------------------

export function discordGuildIconUrl(guildId: string, iconHash: string | null): string | null {
  if (iconHash === null) return null;
  // Animated icons start with `a_`; respect that for proper rendering.
  const ext = iconHash.startsWith('a_') ? 'gif' : 'png';
  return `https://cdn.discordapp.com/icons/${guildId}/${iconHash}.${ext}`;
}

// Single source of truth for the columns the portal reads off
// discord_connections. Token columns are deliberately omitted (column-level
// revoked from authenticated/anon by migration 0010). Keep in sync with
// CLIENT_FACING_COLUMNS in apps/edge/functions/discord-oauth/index.ts.
export const DISCORD_CONNECTION_CLIENT_COLUMNS: readonly string[] = Object.keys(
  DiscordConnectionRowSchema.shape,
);

export const DISCORD_CONNECTION_CLIENT_COLUMNS_CSV =
  DISCORD_CONNECTION_CLIENT_COLUMNS.join(', ');
